// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { createFileRoute, Link, Outlet, useMatchRoute } from "@tanstack/react-router";
import { Camera, Pencil } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useStoreDetailQuery } from "@/api/hooks/use-stores";
import { PageHeader } from "@/components/layout/page-header";
import { IdPill } from "@/components/shared/id-pill";
import { KeyChip } from "@/components/shared/key-chip";
import { DetailPageSkeleton, PageProgressBar } from "@/components/shared/page-loading";
import { RequirePermission } from "@/components/shared/require-permission";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CompareStoreDialog } from "@/features/stores/compare-stores-dialog";
import { CreateStoreDialog } from "@/features/stores/create-store-dialog";
import { StoreActionsMenu } from "@/features/stores/store-actions-menu";
import { ToggleStoreActiveDialog } from "@/features/stores/toggle-store-active-dialog";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";

export const Route = createFileRoute("/_authenticated/stores/$storeName")({
  validateSearch: z.object({ t: z.string().optional(), f: z.string().optional() }),
  component: () => (
    <RequirePermission permission={PAGE_PERMISSIONS.stores}>
      <StoreDetailLayout />
    </RequirePermission>
  ),
});

/**
 * Chrome shared by the store's read page (`/stores/$storeName`) and its edit page
 * (`/stores/$storeName/edit`): the identity pill row, the actions menu and its dialogs.
 * Keeping it in the layout means the header stays mounted when moving between the two.
 */
function StoreDetailLayout() {
  const { storeName } = Route.useParams();
  const { t } = useTranslation("back-office");
  const { hasPermission } = useAuth();
  const matchRoute = useMatchRoute();
  const { data: storeRaw, isLoading, isError } = useStoreDetailQuery(storeName);

  // The edit page owns the Edit / Operations / Hardware tabs, so the button that opens it is
  // hidden once we're there — the store-name breadcrumb is the way back.
  const isEditPage = !!matchRoute({ to: "/stores/$storeName/edit" });
  const canOpenEdit =
    hasPermission(PAGE_PERMISSIONS.storesEdit) ||
    hasPermission(PAGE_PERMISSIONS.storesOperations) ||
    hasPermission(PAGE_PERMISSIONS.storesHardware);

  const store = storeRaw as Record<string, unknown> | undefined;
  const displayName = (store?.legibleName as string) ?? storeName;
  const chainName = store?.chainName as string | undefined;
  const timeZone = store?.details as { timeZone?: string } | undefined;
  const isActive = store?.isActive as boolean | undefined;
  const cameraCount = (store?.cameras as { activeCount?: number } | undefined)?.activeCount;

  const breadcrumbs = useMemo(
    () =>
      isEditPage
        ? [
            { label: t("stores.title"), to: "/stores" },
            { label: displayName, to: `/stores/${storeName}`, bold: true },
          ]
        : [{ label: t("stores.title"), to: "/stores" }],
    [t, isEditPage, displayName, storeName],
  );

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [toggleStoreOpen, setToggleStoreOpen] = useState(false);

  if (isLoading) {
    return (
      <div>
        <PageProgressBar />
        <DetailPageSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <PageHeader title={storeName} breadcrumbs={[{ label: t("stores.title"), to: "/stores" }]} />
        <div className="p-6">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            {t("errors.loadFailed")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={isEditPage ? t("buttons.edit") : displayName}
        subtitle={
          <>
            {store?.storeId !== undefined && <IdPill id={store.storeId as number} />}
            {chainName && <KeyChip label={chainName} variant="chain" />}
            <KeyChip label={storeName} variant="store" />
            {store?.isActive !== undefined && <StatusIndicator active={store.isActive as boolean} />}
            {cameraCount !== undefined && (
              <Badge variant="secondary" className="gap-1.5 font-normal text-xs [&>svg]:size-2.5">
                <Camera />
                {t("stores.cameraCount", { count: cameraCount })}
              </Badge>
            )}
            <div className="ml-auto -my-1.5 flex items-center gap-2">
              {/* 30px rather than the `sm` 32px — sits a touch tighter in the pill row */}
              {canOpenEdit && !isEditPage && (
                <Button asChild size="sm" className="h-7.5">
                  <Link to="/stores/$storeName/edit" params={{ storeName }} search={{}}>
                    <Pencil className="h-4 w-4" />
                    {t("buttons.edit")}
                  </Link>
                </Button>
              )}
              <StoreActionsMenu
                isActive={isActive ?? false}
                onDuplicate={() => setCreateDialogOpen(true)}
                onCompareSettings={() => setCompareDialogOpen(true)}
                onToggleActive={() => setToggleStoreOpen(true)}
              />
            </div>
          </>
        }
        breadcrumbs={breadcrumbs}
      />

      <Outlet />

      <CreateStoreDialog
        key={`duplicate-${storeName}`}
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        duplicateFrom={{
          copyFrom: storeName,
          chainName: chainName ?? "",
          timeZone: timeZone?.timeZone ?? "",
        }}
      />

      <CompareStoreDialog defaultStoreName={storeName} open={compareDialogOpen} onOpenChange={setCompareDialogOpen} />

      <ToggleStoreActiveDialog
        store={
          toggleStoreOpen && store
            ? {
                storeName,
                legibleName: displayName,
                isActive: isActive ?? false,
              }
            : null
        }
        onClose={() => setToggleStoreOpen(false)}
      />
    </div>
  );
}
